import React from 'react';
import { compose } from 'redux';
import classnames from 'classnames';
import { withTranslation, WithTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-types-papi';
import IconButtonWithContextMenu from '@wix/da-shared-react/pkg/Dropdown/Preset/IconButtonWithContextMenu';
import { DropdownItem } from '@wix/da-shared-react/pkg/Dropdown/types';
import {
  isDeviationTextEditable,
  getDeviationType,
  getDeviationResourceType,
} from '@wix/da-shared-react/pkg/DeviationViews/_helpers';
import { DeviationTypes } from '@wix/da-shared-react/pkg/types/deviation';
import EllipsisIcon from '@wix/da-shared-react/pkg/Icons/Ellipsis';
import PinIcon from '@wix/da-shared-react/pkg/Icons/24x24/Pin';
import ConfirmationModal from '@wix/da-shared-react/pkg/Popup/ConfirmationModal';
import { BiEventType } from '@wix/da-shared-react/pkg/biLogger/constants';
import {
  withLogBiEvent,
  WithLogBiEventProps,
} from '@wix/da-shared-react/pkg/biLogger/hocs/withLogBiEvent';
import { PremiumTrash } from '@wix/da-shared-react/pkg/Icons/Confirmations';

import s from './PostEditableItem.scss';

export interface Props {
  item: PapiDeviation;
  className?: string;
  isDraft?: boolean;
  isPinned?: boolean;
  isCurrentUserProfileOwner?: boolean;
  children: React.ReactNode;
  onPin?: () => {};
  onUnpin?: () => {};
  onEdit: () => {};
  onDelete: () => {};
}

export interface State {
  showDeleteConfirmation: boolean;
}

export class PostEditableItem extends React.Component<
  Props & WithTranslation & WithLogBiEventProps,
  State
> {
  readonly state: State = {
    showDeleteConfirmation: false,
  };
  render() {
    const {
      className,
      isPinned,
      isDraft,
      isCurrentUserProfileOwner,
      children,
      t,
    } = this.props;
    return (
      <>
        <div className={classnames(s['root'], className)}>
          {isCurrentUserProfileOwner && (
            <>
              {this.getIsPinable() && isPinned && (
                <PinIcon withPadding className={s['pinned-icon']} />
              )}
              <IconButtonWithContextMenu
                className={s['edit-menu']}
                items={this.getMenuItems()}
                withButton
                noIconHoverEffect
                onOpen={this.onDropdownOpen}
              >
                <EllipsisIcon withPadding />
              </IconButtonWithContextMenu>
            </>
          )}
          {isDraft ? this.renderDraft() : children}
        </div>
        <ConfirmationModal
          isOpen={this.state.showDeleteConfirmation}
          title={this.getDeleteConfirmationTitle()}
          text={t('delete.cantGoBack')}
          icon={<PremiumTrash />}
          confirmBtnLabel={t('widgets.post.delete')}
          confirmBtnType="danger"
          onConfirm={this.onDelete}
          onCancel={this.hideDeleteConfirmation}
        />
      </>
    );
  }

  onDropdownOpen = () => {
    const { logBiEvent, item } = this.props;
    logBiEvent({
      evid: BiEventType.MORE_MENU_CLICK,
      typeid: getDeviationResourceType(item),
      itemid: item.deviationId,
    });
  };

  getDeleteConfirmationTitle() {
    const { t, item } = this.props;
    const deviationType = getDeviationType(item);
    const typeMap = {
      [DeviationTypes.POLL]: t('common.polls'),
      [DeviationTypes.STATUS]: t('common.statuses'),
      [DeviationTypes.JOURNAL]: t('common.journals'),
    };
    const typeName = typeMap.hasOwnProperty(deviationType)
      ? typeMap[deviationType]
      : t('common.deviations');
    return t('delete.confirm', { type: typeName });
  }

  renderDraft = () => (
    <div className={s['draft']} onClick={this.props.onEdit}>
      {this.props.children}
    </div>
  );

  getMenuItems = () => {
    const { isPinned, onPin, onUnpin, onEdit, item, t } = this.props;
    return [
      isDeviationTextEditable(item) && {
        label: t!('widgets.post.edit'),
        onClick: onEdit,
      },
      this.getIsPinable() && {
        label: isPinned
          ? t!('widgets.post.unpinFromHome')
          : t!('widgets.post.pinToHome'),
        onClick: isPinned ? onUnpin : onPin,
      },
      {
        label: t!('widgets.post.delete'),
        onClick: this.showDeleteConfirmation,
      },
    ].filter(Boolean) as DropdownItem[];
  };

  getIsPinable = () => {
    const { isDraft } = this.props;
    return !isDraft;
  };

  onDelete = () => {
    this.hideDeleteConfirmation();
    this.props.onDelete();
  };

  showDeleteConfirmation = () => {
    this.setState({ showDeleteConfirmation: true });
  };

  hideDeleteConfirmation = () => {
    this.setState({ showDeleteConfirmation: false });
  };
}

const enhance = compose(withTranslation(), withLogBiEvent);

export default enhance(PostEditableItem) as React.ComponentType<Props>;
